import { useCallback, useRef, useState } from "react";

import { aiStream } from "@/lib/api";

export interface StreamMeta {
  conversationId?: string;
  modelId?: string;
  costUsd?: number;
  savedNodeId?: string;
  error?: string;
}

export type StreamPhase = "idle" | "streaming" | "done" | "error";

/** Shown when an /api/ai/* stream closes normally (no thrown network error,
 * no `error` SSE frame) but never produced an answer OR a successful `done`
 * frame — e.g. the backend emits a `retrieval` event with zero hits and then
 * just ends the connection. Without this, the turn is appended with empty
 * text and no error, so the assistant panel silently shows nothing. This is
 * already curated, non-technical, client-authored copy (not a raw backend
 * payload) — safe to render verbatim wherever a turn's `error` is shown. */
export const STREAM_EMPTY_ERROR =
  "The assistant couldn't generate a response — it may be unavailable. Try again.";

/** Consume an /api/ai/* SSE stream into reactive text + metadata. */
export function useAiStream() {
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<StreamPhase>("idle");
  const [meta, setMeta] = useState<StreamMeta>({});
  const abortRef = useRef<AbortController | null>(null);
  // Coalesce per-token deltas into at most one render per animation frame.
  // The SSE stream emits tokens far faster than the screen refreshes, and every
  // render re-runs the full markdown + KaTeX pipeline over the WHOLE answer so
  // far — so a per-token setText made one answer O(n²) main-thread work (and
  // stuttered typing in other tabs while streaming). Buffering the latest text
  // and flushing on a rAF caps it to ~60 renders/sec; the final, complete
  // buffer is always committed when the stream ends (see `start`).
  const pendingRef = useRef<string | null>(null);
  const rafRef = useRef<number | null>(null);

  const cancelFlush = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingRef.current = null;
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    cancelFlush();
  }, [cancelFlush]);

  const start = useCallback(
    async (path: "/api/ai/explain" | "/api/ai/rag", body: unknown): Promise<{ text: string; meta: StreamMeta }> => {
      stop();
      const controller = new AbortController();
      abortRef.current = controller;
      setText("");
      setMeta({});
      setPhase("streaming");

      let buffer = "";
      let gotDone = false;
      const collected: StreamMeta = {};

      // Render the newest buffer at most once per frame, collapsing a burst of
      // tokens that land within one frame into a single markdown re-parse.
      const scheduleFlush = () => {
        pendingRef.current = buffer;
        if (rafRef.current !== null) return;
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          if (pendingRef.current !== null) {
            setText(pendingRef.current);
            pendingRef.current = null;
          }
        });
      };

      try {
        for await (const event of aiStream(path, body, controller.signal)) {
          if (event.type === "delta") {
            buffer += event.text;
            scheduleFlush();
          } else if (event.type === "done") {
            gotDone = true;
            collected.conversationId = event.conversation_id ?? collected.conversationId;
            collected.modelId = event.model_id;
            collected.costUsd = event.usage_cost_usd;
          } else if (event.type === "saved") {
            collected.savedNodeId = event.node_id;
          } else if (event.type === "error") {
            collected.error = event.error ?? "stream error";
          }
          // Any other event type (e.g. `retrieval`) is informational only —
          // ignored here, but its presence means the loop below is NOT the
          // "stream produced nothing at all" case handled next.
        }
        if (collected.error) {
          setPhase("error");
        } else if (!gotDone && buffer.trim().length === 0) {
          // The connection closed without a `done` frame AND without any
          // delta text — treat exactly like an explicit `error` event.
          collected.error = STREAM_EMPTY_ERROR;
          setPhase("error");
        } else {
          setPhase("done");
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          collected.error = err instanceof Error ? err.message : String(err);
          setPhase("error");
        } else {
          setPhase("idle");
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
      // Commit the final, complete buffer in one render — even if the last
      // tokens arrived between frames so the coalesced flush never ran — but
      // never clobber a newer stream that superseded this one via stop()/start()
      // (that path aborts this controller's signal).
      if (!controller.signal.aborted) {
        cancelFlush();
        setText(buffer);
        setMeta(collected);
      }
      return { text: buffer, meta: collected };
    },
    [stop, cancelFlush],
  );

  return { text, phase, meta, start, stop };
}
